import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle, RefreshCw, Eye, Search, Filter, MoreVertical, ChevronDown, Users } from 'lucide-react';
import { apiFetch } from '../services/api';
import './Cobrancas.css';

// ⚠️ AJUSTE AQUI se o valor real do role "master" no seu banco for diferente
// (ex: 'admin', 'administrador', etc). Comparação é case-insensitive.
const MASTER_ROLES = ['master', 'admin'];

// ⚠️ Define qual campo identifica um cliente de forma única entre os pagamentos.
// Ordem de prioridade: customerId > customerCpf > customerEmail > customerName.
// Se o backend expõe um ID mais confiável (ex: p.customer?.id), ajuste aqui.
// Deve ser IDÊNTICA à função usada em CobrancasTodas.js.
function getClientKey(p) {
  return (
    p.customerId ||
    p.customerCpf ||
    p.cpf ||
    p.customerEmail ||
    p.customerName ||
    'desconhecido'
  );
}

export default function CobrancasParcelamentos() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // ✅ Detecção de role baseada em user.role / user.roles (vindo de /auth/me)
  const userRole = (user?.role || user?.roles?.[0] || '').toLowerCase();
  const isMaster = MASTER_ROLES.includes(userRole);
  const isAtendente = !isMaster;

  const [pagamentos, setPagamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [buscaRealizada, setBuscaRealizada] = useState(false);
  const [multiploClientes, setMultiploClientes] = useState(false);

  // Carrega pagamentos automaticamente APENAS se for Master
  useEffect(() => {
    if (isAtendente) {
      setLoading(false);
      setPagamentos([]);
      return;
    }

    const carregarPagamentos = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiFetch('/api/asaas/local-payments?limit=500');
        const dados = response.data || [];
        // Filtra apenas parcelamentos (INSTALLMENT)
        const parcelamentos = dados.filter(p =>
          p.subscription_type === 'INSTALLMENT' ||
          (p.description && p.description.toLowerCase().includes('parcela'))
        );
        setPagamentos(parcelamentos);
      } catch (err) {
        console.error(err);
        setError('Não foi possível carregar os parcelamentos.');
      } finally {
        setLoading(false);
      }
    };
    carregarPagamentos();
  }, [isAtendente]);

  // ✅ Função de busca via API (para Atendente) — só exibe resultados se
  // TODOS os parcelamentos encontrados pertencerem ao MESMO cliente.
  const realizarBusca = async () => {
    const term = searchTerm.trim();

    if (!term) {
      setPagamentos([]);
      setBuscaRealizada(true);
      setMultiploClientes(false);
      return;
    }

    setLoading(true);
    setError(null);
    setMultiploClientes(false);
    try {
      const response = await apiFetch(`/api/asaas/local-payments?limit=500&search=${encodeURIComponent(term)}`);
      let dados = response.data || [];

      // Filtro manual para garantir resultados relevantes
      const termLower = term.toLowerCase();
      dados = dados.filter(p => {
        const name = (p.customerName || '').toLowerCase();
        const email = (p.customerEmail || '').toLowerCase();
        const cpf = (p.customerCpf || p.cpf || '').replace(/\D/g, '');
        const description = (p.description || '').toLowerCase();
        const termDigits = term.replace(/\D/g, '');

        const match = name.includes(termLower) ||
                      email.includes(termLower) ||
                      description.includes(termLower) ||
                      (termDigits && cpf.includes(termDigits));

        return match;
      });

      // Filtra pagamentos que fazem parte de um parcelamento
      const parcelamentos = dados.filter(p =>
        p.installment || // Se tiver o ID do plano de parcelamento (ex: ins_xxx)
        p.subscription_type === 'INSTALLMENT' ||
        (p.description && p.description.toLowerCase().includes('parcela'))
      );

      // ✅ Verifica se os resultados pertencem todos ao mesmo cliente
      const chavesUnicas = new Set(parcelamentos.map(getClientKey));

      if (chavesUnicas.size > 1) {
        // Busca ambígua: encontrou mais de um cliente diferente
        setPagamentos([]);
        setMultiploClientes(true);
      } else {
        setPagamentos(parcelamentos);
        setMultiploClientes(false);
      }
      setBuscaRealizada(true);
    } catch (err) {
      console.error(err);
      setError('Não foi possível realizar a busca.');
      setPagamentos([]);
      setBuscaRealizada(true);
    } finally {
      setLoading(false);
    }
  };

  const formatarData = (dataStr) => {
    if (!dataStr) return '—';
    const d = new Date(dataStr + 'T00:00:00');
    return d.toLocaleDateString('pt-BR');
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
  };

  const getStatusBadge = (status) => {
    const classes = {
      'RECEIVED': 'cob-status-received',
      'CONFIRMED': 'cob-status-confirmed',
      'PENDING': 'cob-status-pending',
      'OVERDUE': 'cob-status-overdue'
    };
    const labels = {
      'RECEIVED': 'Recebido',
      'CONFIRMED': 'Confirmado',
      'PENDING': 'Pendente',
      'OVERDUE': 'Vencido'
    };
    return <span className={`cob-status-badge ${classes[status] || ''}`}>{labels[status] || status}</span>;
  };

  const handleCancelInstallment = async (installmentId, customerName) => {
    if (!installmentId) {
      alert('Não foi possível identificar o ID do parcelamento para cancelar.');
      return;
    }

    if (!window.confirm(`Tem certeza que deseja CANCELAR todo o parcelamento de ${customerName}?\n\nIsso cancelará todas as parcelas futuras pendentes de uma só vez.`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await apiFetch(`/api/asaas/installments/${installmentId}`, {
        method: 'DELETE'
      });

      if (response.success) {
        alert('✅ Parcelamento cancelado com sucesso!');
        // Recarrega a lista
        if (isAtendente) {
          realizarBusca();
        } else {
          const resp = await apiFetch('/api/asaas/local-payments?limit=500');
          const dados = resp.data || [];
          const parcelamentos = dados.filter(p =>
            p.installment || p.subscription_type === 'INSTALLMENT' || (p.description && p.description.toLowerCase().includes('parcela'))
          );
          setPagamentos(parcelamentos);
        }
      } else {
        alert('Erro: ' + (response.error || 'Não foi possível cancelar.'));
      }
    } catch (err) {
      console.error('Erro ao cancelar:', err);
      alert('Erro ao cancelar parcelamento.');
    } finally {
      setLoading(false);
    }
  };

  // Estados de exibição para o fluxo do Atendente
  const mostrarPromptBuscaInicial = isAtendente && !buscaRealizada && !loading && !error;
  const mostrarAvisoMultiploClientes = isAtendente && buscaRealizada && multiploClientes && !loading;
  const mostrarTabelaOuVazio = (!isAtendente || (buscaRealizada && !multiploClientes));

  return (
    <div className="cobrancas-page-container">
      <div className="cobrancas-page-header">
        <h1 className="cobrancas-page-title">Parcelamentos</h1>
      </div>

      <div className="cobrancas-card">
        {/* Toolbar */}
        <div className="cobrancas-toolbar">
          <div className="cobrancas-toolbar-left">
            <div className="cobrancas-search-box">
              <input
                type="text"
                placeholder={isAtendente ? "Digite o nome, email ou CPF do cliente para buscar" : "Procurar por nome ou email do cliente"}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    realizarBusca();
                  }
                }}
                className="cobrancas-search-input"
              />
              <button
                className="cobrancas-search-btn"
                title="Buscar"
                onClick={realizarBusca}
              >
                <Search size={16} />
              </button>
            </div>
            {isMaster && (
              <button className="cobrancas-btn-filter">
                <Filter size={16} />
                Filtros
                <ChevronDown size={16} />
              </button>
            )}
          </div>
          <div className="cobrancas-toolbar-right">
            {isMaster && (
              <button className="cobrancas-btn-batch">
                <MoreVertical size={16} />
                Ações em lote
                <ChevronDown size={16} />
              </button>
            )}
            <button className="cobrancas-btn-add" onClick={() => navigate('/gerar-cobranca?tipo=parcelamento')}>
              <span style={{ fontSize: 18, marginRight: 4 }}>+</span>
              Adicionar cobrança
            </button>
          </div>
        </div>

        {loading && (
          <div className="cobrancas-loading">
            <RefreshCw size={24} className="spinning" />
            <p>Carregando parcelamentos...</p>
          </div>
        )}

        {error && (
          <div className="cobrancas-error">
            <AlertCircle size={24} />
            <p>{error}</p>
          </div>
        )}

        {/* Mensagem inicial para Atendente */}
        {mostrarPromptBuscaInicial && (
          <div className="cobrancas-empty">
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              padding: '40px 20px',
              color: '#64748b'
            }}>
              <Search size={48} style={{ color: '#94a3b8' }} />
              <h3 style={{ margin: 0, color: '#334155', fontSize: '18px' }}>
                Realize uma busca para visualizar os parcelamentos
              </h3>
              <p style={{ margin: 0, textAlign: 'center', maxWidth: '400px', fontSize: '14px' }}>
                Digite o nome, email ou CPF do cliente no campo de busca acima e pressione Enter ou clique no ícone de lupa.
              </p>
            </div>
          </div>
        )}

        {/* Aviso de busca ambígua — encontrou mais de um cliente diferente */}
        {mostrarAvisoMultiploClientes && (
          <div className="cobrancas-empty">
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              padding: '40px 20px',
              color: '#92400e'
            }}>
              <Users size={48} style={{ color: '#f59e0b' }} />
              <h3 style={{ margin: 0, color: '#78350f', fontSize: '18px' }}>
                Sua busca encontrou mais de um cliente
              </h3>
              <p style={{ margin: 0, textAlign: 'center', maxWidth: '440px', fontSize: '14px' }}>
                Para sua segurança, só é possível visualizar parcelamentos de um cliente por vez.
                Refine a busca com o CPF completo ou o email exato do cliente.
              </p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && mostrarTabelaOuVazio && pagamentos.length === 0 && (buscaRealizada || !isAtendente) && (
          <div className="cobrancas-getstarted">
            <div className="cobrancas-getstarted-illustration">
              <svg width="180" height="150" viewBox="0 0 180 150" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M38 122c-14-6-20-20-16-34 4-13 16-20 28-20h6c4-10 14-16 24-16 12 0 22 8 25 19h3c11 0 20 9 20 20v6c0 13-10 25-25 25H60c-8 0-16-2-22 0z" fill="#f4a261" />
                <rect x="46" y="46" width="34" height="22" rx="4" fill="#1d4ed8" transform="rotate(-14 46 46)" />
                <rect x="46" y="52" width="34" height="4" fill="#93c5fd" transform="rotate(-14 46 52)" />
                <path d="M78 60l16-8c4 8-1 17-9 20l-16 6c-3-8 1-15 9-18z" fill="#84cc16" />
                <path d="M84 68l14-9" stroke="#3f6212" strokeWidth="2" strokeLinecap="round" />
                <rect x="122" y="30" width="26" height="26" rx="7" fill="#93c5fd" />
                <text x="135" y="48" fontSize="15" fontWeight="700" fill="#1e3a8a" textAnchor="middle" fontFamily="Arial, sans-serif">$</text>
                <rect x="126" y="66" width="22" height="22" rx="6" fill="#2563eb" transform="rotate(45 137 77)" />
              </svg>
            </div>
            <h2 className="cobrancas-getstarted-title">Comece a cobrar seus clientes com o Asaas</h2>
            <p className="cobrancas-getstarted-description">
              Uma cobrança parcelada é quando você divide o valor total da cobrança em parcelas para o seu cliente, assim ele não precisa pagar o valor de uma só vez.
            </p>
            <button className="cobrancas-getstarted-cta" onClick={() => navigate('/gerar-cobranca?tipo=parcelamento')}>
              Criar parcelamento
            </button>
          </div>
        )}

        {/* Table */}
        {!loading && !error && mostrarTabelaOuVazio && pagamentos.length > 0 && (buscaRealizada || !isAtendente) && (
          <div className="cobrancas-table-wrapper">
            <table className="cobrancas-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Valor</th>
                  <th>Vencimento</th>
                  <th>Status</th>
                  <th>Descrição</th>
                  <th>Data Pagamento</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {pagamentos.map(p => (
                  <tr key={p.id}>
                    <td>{p.customerName || '—'}</td>
                    <td className="valor">{formatarMoeda(p.value)}</td>
                    <td>{formatarData(p.dueDate)}</td>
                    <td>{getStatusBadge(p.status)}</td>
                    <td>{p.description || '—'}</td>
                    <td>{formatarData(p.receivedDate || p.paymentDate)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="cob-btn-view"
                          title="Ver detalhes"
                          onClick={() => window.open(p.invoiceUrl || '#', '_blank')}
                        >
                          <Eye size={16} />
                        </button>

                        {/* Botão de Cancelar Parcelamento (só aparece se houver ID do parcelamento e estiver pendente/vencido) */}
                        {p.installment && (p.status === 'PENDING' || p.status === 'OVERDUE') && (
                          <button
                            className="cob-btn-view"
                            style={{ color: '#ef4444', borderColor: '#fecaca' }}
                            title="Cancelar todo o parcelamento"
                            onClick={() => handleCancelInstallment(p.installment, p.customerName)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 6h18"></path>
                              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
